/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import type { PointerEvent, ReactElement, ReactNode } from "react";

import { cssFns } from "@superweb/css";
import { Button, icons, LinkButton, useTypo, useUiColors } from "@superweb/ui";

type ButtonProps = {
  text: string;
  view?: "default" | "action" | "outline";
  progress?: boolean;
  onPress?: () => void;
  link?: {
    href: string;
    onClick?: (e: PointerEvent<HTMLAnchorElement>) => void;
    external?: boolean;
  };
  guidanceWrapper?: ({ children }: { children: ReactNode }) => ReactElement;
};

const ButtonContent = ({ link, progress, ...restProps }: ButtonProps) => {
  return (
    <div
      css={{
        display: "grid",
        minWidth: "180px",
      }}
    >
      {link ? (
        <LinkButton {...restProps} link={link} />
      ) : (
        <Button {...restProps} progress={progress} />
      )}
    </div>
  );
};

export type EmptyStatePlaceholderProps = {
  title: string;
  titleId?: string;
  description: string;
  buttons?: ButtonProps[];
  link?: {
    text: string;
    href: string;
    target?: string;
  };
  size?: "small" | "medium";
  image?: ReactNode;
  variant?: "waitingMan" | "laptop" | "default" | "writing";
};

export const useEmptyStatePlaceholder = ({
  title,
  description,
  image,
  buttons,
  links,
  variant = "default",
}: {
  title: string;
  titleId?: string;
  description?: string;
  buttons?: ButtonProps[];
  links?: {
    text: string;
    href: string;
    target?: string;
    variant?: "default" | "ghost";
  }[];
  size?: "small" | "medium";
} & {
  image?: ReactNode;
  variant?: "waitingMan" | "laptop" | "default" | "writing";
  size?: "small" | "medium";
}) => {
  const variants = {
    waitingMan: {
      src: new URL("images/waiting-man.svg", import.meta.url).href,
    },
    laptop: { src: new URL("images/laptop.svg", import.meta.url).href },
    default: {
      srcSet: `
        ${new URL("images/notebook.png", import.meta.url).href}
        ${new URL("images/notebook1.5x.png", import.meta.url).href} 1.5x, 
        ${new URL("images/notebook2x.png", import.meta.url).href} 2x
    `,
    },
    writing: {
      src: new URL("images/writing.svg", import.meta.url).href,
    },
  };

  return {
    title,
    description,
    buttons,
    links,
    image: image ? image : <img {...variants[variant]} />,
  };
};

/**
 * Placeholder for empty state
 */
export const EmptyStatePlaceholder: {
  (
    props: {
      title: ReactNode;
      titleId?: string;
      description?: ReactNode;
      buttons?: ButtonProps[];
      link?: {
        text: string;
        href: string;
        target?: string;
      };
      size?: "small" | "medium";
    } & {
      image?: ReactNode;
      variant?: "waitingMan" | "laptop" | "default" | "writing";
    },
  ): JSX.Element;
} = ({
  title,
  titleId,
  description,
  image,
  buttons,
  link,
  variant = "default",
  size = "medium",
}: {
  title: ReactNode;
  titleId?: string;
  description?: ReactNode;
  buttons?: ButtonProps[];
  link?: {
    text: string;
    href: string;
    target?: string;
  };
  size?: "small" | "medium";
} & {
  image?: ReactNode;
  variant?: "waitingMan" | "laptop" | "default" | "writing";
  size?: "small" | "medium";
}) => {
  const typo = useTypo();
  const uiColors = useUiColors();

  const variants = {
    waitingMan: {
      src: new URL("images/waiting-man.svg", import.meta.url).href,
    },
    laptop: { src: new URL("images/laptop.svg", import.meta.url).href },
    default: {
      srcSet: `
        ${new URL("images/notebook.png", import.meta.url).href}
        ${new URL("images/notebook1.5x.png", import.meta.url).href} 1.5x, 
        ${new URL("images/notebook2x.png", import.meta.url).href} 2x
    `,
    },
    writing: {
      src: new URL("images/writing.svg", import.meta.url).href,
    },
  };

  const titleLevel = (
    {
      small: "body1",
      medium: "title4",
    } as const
  )[size];

  return (
    <div
      css={{
        ...cssFns.margin("auto"),
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div
        css={{
          display: "grid",
          rowGap: "20px",
          justifyItems: "center",
          maxWidth: "732px",
          textAlign: "center",
        }}
      >
        {image === undefined ? (
          <img
            {...variants[variant]}
            alt=""
            css={{
              maxWidth: "100%",
            }}
          />
        ) : (
          image
        )}
        <div css={{ display: "grid", rowGap: "2px" }}>
          <h2
            id={titleId}
            css={{
              ...typo({
                level: titleLevel,
                weight: "medium",
                density: "normal",
              }),
              ...cssFns.margin("0"),
            }}
          >
            {title}
          </h2>
          {description ? (
            <span
              css={typo({
                level: "body2",
                weight: "regular",
                density: "normal",
              })}
            >
              {description}
            </span>
          ) : null}
        </div>

        {!!buttons?.length && (
          <div
            css={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              ...cssFns.gap("8px"),
            }}
          >
            {buttons.map((button, index) => {
              return button.guidanceWrapper ? (
                <button.guidanceWrapper key={index}>
                  <ButtonContent {...button} />
                </button.guidanceWrapper>
              ) : (
                <ButtonContent key={index} {...button} />
              );
            })}
          </div>
        )}

        {link && (
          <a
            href={link.href}
            target={link.target}
            css={{
              display: "grid",
              gridAutoFlow: "column",
              alignItems: "center",
              columnGap: "4px",
              ...cssFns.padding("12px"),
              ...typo({
                level: "body2",
                weight: "regular",
                density: "normal",
              }),
              color: uiColors.textMinor,
            }}
          >
            {link.text}
            {link.target === "_blank" && <icons.NewTab />}
          </a>
        )}
      </div>
    </div>
  );
};
